import React, { useState } from "react";
import { apple, ORANGE, GREEN, BLUE, Icons, Btn, Badge } from "./shared";

/* ============================================================
   Quick Create Menu — opens dropdown then slide-in sheet
   ============================================================ */
export default function CreateMenu() {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(null); // shipment | quote | customer | driver | invoice | payment | incident | announce

  const items = [
    { id: "shipment", label: "Create Shipment", desc: "Plan stops, equipment, pricing", icon: Icons.truck, kbd: "S" },
    { id: "quote", label: "Create Quote", desc: "Get AI-recommended rate", icon: Icons.bolt, kbd: "Q" },
    { id: "customer", label: "Add Customer", desc: "New shipper account", icon: Icons.users, kbd: "C" },
    { id: "driver", label: "Add Driver / Carrier", desc: "Onboard with compliance docs", icon: Icons.person, kbd: "D" },
    { id: "invoice", label: "Create Invoice", desc: "Generate AR from shipment", icon: Icons.doc, kbd: "I" },
    { id: "payment", label: "Record Payment", desc: "Apply payment to invoice", icon: Icons.dollar, kbd: "P" },
    { id: "incident", label: "Open Incident", desc: "Claim, OS&D, accident", icon: Icons.alert, kbd: "X" },
    { id: "announce", label: "Send Announcement", desc: "Broadcast to drivers / customers", icon: Icons.send, kbd: "A" },
  ];

  return (
    <>
      <div style={{ position: "relative" }}>
        <Btn primary testid="web-create-btn" onClick={() => setOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px" }}>
          <span style={{ display: "inline-flex" }}>{Icons.plus}</span>
          Create
          <span style={{ opacity: 0.65, fontSize: 9.5 }}>▾</span>
        </Btn>
        {open && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div className="ry-card" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 41, width: 340, background: "rgba(20,20,26,0.97)", backdropFilter: "blur(24px)", border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 14, boxShadow: "0 20px 50px rgba(0,0,0,0.6)", padding: 8, fontFamily: apple, animationDuration: "180ms" }}>
              <div style={{ padding: "8px 12px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 10.5, letterSpacing: 1.4, color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>QUICK CREATE</div>
                <span style={{ fontSize: 9.5, color: "rgba(255,255,255,0.4)" }}>⌘ ⇧ N</span>
              </div>
              {items.map((it) => (
                <button key={it.id} data-testid={`create-${it.id}`}
                  onClick={() => { setSheet(it.id); setOpen(false); }}
                  style={{ width: "100%", background: "none", border: "none", color: "white", padding: "8px 10px", borderRadius: 9, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontFamily: apple, textAlign: "left" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,107,0,0.10)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, color: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{it.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>{it.label}</div>
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{it.desc}</div>
                  </div>
                  <span style={{ background: "rgba(255,255,255,0.08)", padding: "2px 6px", borderRadius: 5, fontSize: 9.5, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>⌘{it.kbd}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {sheet && <CreateSheet kind={sheet} onClose={() => setSheet(null)} />}
    </>
  );
}

/* ============================================================
   Slide-in sheet — macOS-style modal from the right
   ============================================================ */
function CreateSheet({ kind, onClose }) {
  const cfg = {
    shipment: { title: "New Shipment", kicker: "OPERATIONS", body: <ShipmentForm /> },
    quote: { title: "New AI Quote", kicker: "PRICING", body: <QuoteForm /> },
    customer: { title: "Add Customer", kicker: "CRM", body: <CustomerForm /> },
    driver: { title: "Add Driver / Carrier", kicker: "FLEET", body: <DriverForm /> },
    invoice: { title: "Create Invoice", kicker: "BILLING", body: <InvoiceForm /> },
    payment: { title: "Record Payment", kicker: "AR", body: <PaymentForm /> },
    incident: { title: "Open Incident", kicker: "RISK", body: <IncidentForm /> },
    announce: { title: "Send Announcement", kicker: "BROADCAST", body: <AnnounceForm /> },
  }[kind];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, fontFamily: apple, color: "white" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", animation: "ry-fade-in 200ms" }} />
      <div className="ry-sheet" style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 460, background: "linear-gradient(180deg, #1a1a20, #131318)", borderLeft: "0.5px solid rgba(255,255,255,0.1)", boxShadow: "-30px 0 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 22px", borderBottom: "0.5px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: ORANGE, fontWeight: 700 }}>{cfg.kicker}</div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5, marginTop: 2 }}>{cfg.title}</div>
          </div>
          <button onClick={onClose} data-testid="sheet-close" style={{ background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 7, color: "white", cursor: "pointer", display: "flex" }}>{Icons.x}</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 22 }}>{cfg.body}</div>
        <div style={{ padding: "14px 22px", borderTop: "0.5px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,0,0,0.2)" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>Saved as draft automatically</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn primary onClick={onClose}>Save & Continue</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Form bodies (visual mockups)
   ============================================================ */
function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10.5, letterSpacing: 0.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
function Input({ value, placeholder, prefix }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "9px 12px", display: "flex", alignItems: "center", gap: 8 }}>
      {prefix && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>{prefix}</span>}
      <span style={{ flex: 1, fontSize: 13, color: value ? "white" : "rgba(255,255,255,0.4)" }}>{value || placeholder}</span>
    </div>
  );
}
function Select({ value, placeholder }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "9px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: 13, color: value ? "white" : "rgba(255,255,255,0.4)" }}>{value || placeholder}</span>
      <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 9 }}>▾</span>
    </div>
  );
}
function Row({ children, cols = 2 }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10 }}>{children}</div>;
}

function ShipmentForm() {
  return (
    <>
      <Field label="Customer"><Select value="ABC Manufacturing" /></Field>
      <div style={{ background: "rgba(255,107,0,0.06)", border: `0.5px dashed ${ORANGE}55`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: ORANGE, fontWeight: 700, marginBottom: 8 }}>STOPS · 2</div>
        {[{ k: "PICKUP", v: "12 Industrial Pkwy, Toronto, ON · May 20, 8:00 AM" }, { k: "DROP", v: "451 Cargo Rd, Chicago, IL · May 20, 6:00 PM" }].map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", alignItems: "center" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: i === 0 ? ORANGE : GREEN, color: "white", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: 1, color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>{s.k}</div>
              <div style={{ fontSize: 12.5, color: "white" }}>{s.v}</div>
            </div>
          </div>
        ))}
        <Btn ghost size="sm" style={{ padding: "4px 0" }}>+ Add stop</Btn>
      </div>
      <Row>
        <Field label="Equipment"><Select value="53' Dry Van" /></Field>
        <Field label="Weight"><Input value="38,200" prefix="lbs" /></Field>
      </Row>
      <Row>
        <Field label="Commodity"><Input value="Auto parts (palletized)" /></Field>
        <Field label="Hazmat"><Select value="No" /></Field>
      </Row>
      <Field label="Rate" hint="AI suggests $2,180 – $2,450 based on lane history">
        <Input value="2,300" prefix="$" />
      </Field>
      <div style={{ background: "rgba(16,185,129,0.08)", border: `0.5px solid ${GREEN}55`, borderRadius: 9, padding: 10, fontSize: 11.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: GREEN, fontWeight: 700 }}>✓ Margin projection</span>
        <span style={{ color: "white", fontWeight: 700 }}>+24.6% · $565</span>
      </div>
    </>
  );
}
function QuoteForm() {
  return (
    <>
      <Row>
        <Field label="Origin ZIP"><Input value="M5V 2H1" /></Field>
        <Field label="Dest ZIP"><Input value="60601" /></Field>
      </Row>
      <Row>
        <Field label="Equipment"><Select value="53' Dry Van" /></Field>
        <Field label="Weight (lbs)"><Input value="38,200" /></Field>
      </Row>
      <Row>
        <Field label="Pickup Date"><Input value="May 20, 2025" /></Field>
        <Field label="Service"><Select value="Standard · 1 day" /></Field>
      </Row>
      <div style={{ background: "linear-gradient(135deg, rgba(255,107,0,0.10), rgba(168,85,247,0.10))", border: `0.5px solid ${ORANGE}66`, borderRadius: 12, padding: 14, marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ color: ORANGE, animation: "ry-ai-spin 3s linear infinite" }}>{Icons.cpu}</div>
          <div style={{ fontSize: 11, letterSpacing: 1.4, color: ORANGE, fontWeight: 700 }}>AI PRICING ENGINE</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 }}>
          {[{ l: "Floor", v: "$2,080" }, { l: "Suggested", v: "$2,300", hl: true }, { l: "Ceiling", v: "$2,520" }].map((b) => (
            <div key={b.l} style={{ background: b.hl ? `${ORANGE}22` : "rgba(255,255,255,0.04)", border: b.hl ? `1px solid ${ORANGE}` : "0.5px solid rgba(255,255,255,0.1)", borderRadius: 9, padding: "8px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.6)", letterSpacing: 0.5 }}>{b.l}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: b.hl ? ORANGE : "white", marginTop: 2 }}>{b.v}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
          Based on <strong style={{ color: "white" }}>248 historical lanes</strong>, current fuel index <strong style={{ color: "white" }}>$3.91/gal</strong>, capacity tight (8% above baseline).
        </div>
      </div>
    </>
  );
}
function CustomerForm() {
  return (
    <>
      <Field label="Company"><Input placeholder="ABC Manufacturing Inc." /></Field>
      <Row>
        <Field label="DUNS"><Input value="06-849-3201" /></Field>
        <Field label="Credit Tier"><Select value="A · 30 days" /></Field>
      </Row>
      <Field label="Primary Contact"><Input value="Dwayne Johnson · jen@abcmfg.com" /></Field>
      <Field label="Billing Address"><Input value="100 Industrial Pkwy, Toronto, ON" /></Field>
      <Row>
        <Field label="Payment Terms"><Select value="Net 30" /></Field>
        <Field label="Credit Limit"><Input value="50,000" prefix="$" /></Field>
      </Row>
      <Field label="Preferred Lanes (auto)" hint="System will track lanes after first 3 shipments">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["YYZ ↔ ORD", "YUL ↔ JFK", "YVR ↔ SEA"].map((c) => <Badge key={c} color={ORANGE}>{c}</Badge>)}
        </div>
      </Field>
    </>
  );
}
function DriverForm() {
  return (
    <>
      <Row>
        <Field label="First Name"><Input value="Dwayne" /></Field>
        <Field label="Last Name"><Input value="Whitaker" /></Field>
      </Row>
      <Field label="MC # / DOT #"><Input value="MC-948231 / DOT-3204891" /></Field>
      <Row>
        <Field label="Equipment"><Select value="Kenworth T680" /></Field>
        <Field label="Capacity"><Input value="53' Dry Van" /></Field>
      </Row>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 10.5, letterSpacing: 1, color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 8 }}>COMPLIANCE DOCS REQUIRED</div>
        {[{ k: "MC Authority", ok: true }, { k: "Cargo Insurance ($100K)", ok: true }, { k: "W-9 / TIN", ok: false }, { k: "Drug screen (DOT)", ok: false }, { k: "Medical card", ok: false }].map((d) => (
          <div key={d.k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
            <span style={{ color: "white" }}>{d.k}</span>
            <span style={{ color: d.ok ? GREEN : "rgba(255,255,255,0.5)" }}>{d.ok ? "✓ Uploaded" : "Pending upload"}</span>
          </div>
        ))}
      </div>
      <Field label="Bank for ACH payout"><Input value="•••• 4892 · TD Canada Trust" /></Field>
    </>
  );
}
function InvoiceForm() {
  return (
    <>
      <Field label="From Shipment"><Select value="RY-78432 · ABC Manufacturing · $4,250" /></Field>
      <Row>
        <Field label="Invoice #"><Input value="INV-2025-000189" /></Field>
        <Field label="Issue Date"><Input value="May 20, 2025" /></Field>
      </Row>
      <Field label="Customer"><Input value="ABC Manufacturing · Net 30 · A-tier" /></Field>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 10.5, letterSpacing: 1, color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 8 }}>LINE ITEMS</div>
        {[{ k: "Line haul · YYZ → ORD", v: "$3,900" }, { k: "Fuel surcharge (18%)", v: "$280" }, { k: "ACE eManifest", v: "$70" }, { k: "Tax (HST)", v: "$0" }].map((l) => (
          <div key={l.k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
            <span style={{ color: "rgba(255,255,255,0.85)" }}>{l.k}</span>
            <span style={{ color: "white", fontWeight: 700 }}>{l.v}</span>
          </div>
        ))}
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "0.5px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "space-between", fontSize: 14 }}>
          <span style={{ fontWeight: 700 }}>Total</span>
          <span style={{ fontWeight: 800, color: ORANGE }}>$4,250.00</span>
        </div>
      </div>
      <Field label="Delivery"><Select value="Email PDF + portal · auto-remind D+15" /></Field>
    </>
  );
}
function PaymentForm() {
  return (
    <>
      <Field label="Customer"><Select value="ABC Manufacturing" /></Field>
      <Field label="Apply to Invoice"><Select value="INV-2025-000158 · $4,250 · 11 days" /></Field>
      <Row>
        <Field label="Amount"><Input value="4,250.00" prefix="$" /></Field>
        <Field label="Date"><Input value="May 18, 2025" /></Field>
      </Row>
      <Row>
        <Field label="Method"><Select value="ACH" /></Field>
        <Field label="Reference"><Input value="ABC-WIRE-44218" /></Field>
      </Row>
      <Field label="Notes"><Input value="Paid in full, no remits" /></Field>
      <div style={{ background: "rgba(16,185,129,0.08)", border: `0.5px solid ${GREEN}55`, borderRadius: 9, padding: 10, fontSize: 11.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: GREEN, fontWeight: 700 }}>✓ Invoice will be marked PAID</span>
        <span style={{ color: "white", fontWeight: 700 }}>Balance: $0.00</span>
      </div>
    </>
  );
}
function IncidentForm() {
  return (
    <>
      <Field label="Type">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
          {[{ k: "OS&D", c: ORANGE }, { k: "Damage / Claim", c: "#ef4444" }, { k: "Accident", c: "#ef4444" }, { k: "Reefer breakdown", c: BLUE }].map((t, i) => (
            <button key={t.k} style={{ background: i === 1 ? `${t.c}22` : "rgba(255,255,255,0.04)", border: `0.5px solid ${i === 1 ? t.c : "rgba(255,255,255,0.12)"}`, color: i === 1 ? t.c : "white", padding: "8px 10px", borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>{t.k}</button>
          ))}
        </div>
      </Field>
      <Field label="Related Shipment"><Select value="RY-78422 · Memphis, TN" /></Field>
      <Row>
        <Field label="Date"><Input value="May 18, 2025" /></Field>
        <Field label="Severity"><Select value="Medium · $2,400 est" /></Field>
      </Row>
      <Field label="Description"><Input value="Pallet 3 of 14 crushed during offload. Photo evidence attached." /></Field>
      <Field label="Photos / Documents">
        <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px dashed rgba(255,255,255,0.2)", borderRadius: 9, padding: 14, textAlign: "center", color: "rgba(255,255,255,0.55)", fontSize: 11.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span>{Icons.upload}</span> 3 files uploaded · 2.4 MB
        </div>
      </Field>
      <Field label="Assign to"><Select value="Dwayne Johnson · Risk Manager" /></Field>
    </>
  );
}
function AnnounceForm() {
  return (
    <>
      <Field label="Audience">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[{ k: "All Drivers", c: ORANGE }, { k: "RY+ Gold", c: "#f59e0b" }, { k: "All Customers", c: BLUE }, { k: "Enterprise only", c: GREEN }].map((a, i) => (
            <button key={a.k} style={{ background: i === 0 ? `${a.c}22` : "rgba(255,255,255,0.04)", border: `0.5px solid ${i === 0 ? a.c : "rgba(255,255,255,0.12)"}`, color: i === 0 ? a.c : "rgba(255,255,255,0.85)", padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple }}>{a.k}</button>
          ))}
        </div>
      </Field>
      <Field label="Channels">
        <div style={{ display: "flex", gap: 8 }}>
          {[{ k: "Push", on: true }, { k: "SMS", on: true }, { k: "Email", on: false }, { k: "In-app", on: true }].map((c) => (
            <span key={c.k} style={{ background: c.on ? `${ORANGE}22` : "rgba(255,255,255,0.04)", border: `0.5px solid ${c.on ? ORANGE : "rgba(255,255,255,0.12)"}`, color: c.on ? ORANGE : "rgba(255,255,255,0.55)", padding: "5px 11px", borderRadius: 7, fontSize: 11, fontWeight: 700 }}>{c.on ? "✓ " : ""}{c.k}</span>
          ))}
        </div>
      </Field>
      <Field label="Subject"><Input value="New $500 referral bonus for RY+ Gold members" /></Field>
      <Field label="Message">
        <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: 12, fontSize: 12.5, color: "white", minHeight: 100, lineHeight: 1.5 }}>
          Hey team — starting today, every RY+ Gold member earns $500 for each new driver they refer who completes their first 30 loads. No cap. Cash hits your account on day 31.
        </div>
      </Field>
      <Field label="Send"><Select value="Now · 1,247 recipients" /></Field>
    </>
  );
}
